import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ChevronRight,
  ArrowLeft,
  Package,
  User,
  MapPin,
  Truck,
  CreditCard,
  FileText,
  Zap,
  Snowflake,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { message } from 'antd';
import { ROUTE } from '@/config';
import { cn } from '@/lib/utils';
import OrderStepper from '@/components/OrderStepper/OrderStepper';
import useNewOrderStore from '@/stores/useNewOrderStore';
import useProductStore from '@/stores/productStore';
import { useCreateOrder, useGetAvailableCarriers, useListShippingRules } from '@/hooksApi';
import { buildAutoDispatchBannerText } from '@/utils/buildAutoDispatchBannerText';
import { useUpdateOrder } from '@/hooksApi/useOrder';
import { buildAvailableCarriersRequest, normalizeToTranslationKey } from '@/utils';
import { AvailableCarrierItem, AvailableCarriersAdaptedResponse, ServiceTypeEnum } from '@/types';
import { useFetchPickupAddresses } from '@/hooksApi/usePickupAddress';
import fragileIcon from '@/assets/fragile.svg';

const SummaryRow = ({ label, value }: { label: string; value: string }) => (
  <div className='flex items-start justify-between gap-4 py-2'>
    <span className='text-sm text-sd-muted-foreground shrink-0 max-w-[45%]'>{label}</span>
    <span className='text-sm font-medium text-end break-words max-w-[55%]'>{value}</span>
  </div>
);

const SectionHeader = ({ icon: Icon, title }: { icon: LucideIcon; title: string }) => (
  <div className='flex items-center gap-2 mb-4'>
    <Icon className='h-4 w-4 text-sd-muted-foreground' strokeWidth={1.5} />
    <h2 className='text-sm font-bold uppercase tracking-wider text-sd-foreground'>{title}</h2>
  </div>
);

// Label mappers are defined inside the component to access t() for i18n

const formatIraqiPhone = (phone: string | undefined) => {
  if (!phone) return '—';
  // Strip leading + if present
  const digits = phone.replace(/\D/g, '');
  // Iraqi numbers: 964 + 10 digits (e.g. 9647701234567)
  if (digits.startsWith('964') && digits.length === 13) {
    return `+${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 9)} ${digits.slice(9)}`;
  }
  // Fallback: return as-is with + prefix if numeric
  return digits.length > 0 ? `+${digits}` : phone;
};

const titleCase = (s: string) => s.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

const OrderSummary = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === 'rtl';
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const getPaymentLabel = (type: string | undefined) => {
    if (!type) return '—';
    const map: Record<string, string> = {
      COLLECT_ON_DELIVERY: String(t('cod')),
      PREPAID: String(t('prepaid')),
    };
    return map[type] || type;
  };

  const getSizeLabel = (size: string | undefined) => {
    if (!size) return '—';
    const map: Record<string, string> = {
      S: String(t('small')),
      M: String(t('medium')),
      L: String(t('large')),
      XL: String(t('extra_large', 'Extra Large')),
    };
    return map[size] || size;
  };

  const getOrderTypeLabel = (type: string | undefined) => {
    if (!type) return '—';
    const map: Record<string, string> = {
      DEFAULT: String(t('normal')),
      RETURN: String(t('return_order')),
      EXCHANGE: String(t('exchange')),
    };
    return map[type] || type;
  };

  const getPickupLabel = (type: string | undefined) => {
    if (!type) return '—';
    const map: Record<string, string> = {
      PICK_UP: String(t('boxy_collects')),
      DROP_OFF: String(t('drop_off', 'Drop-off')),
    };
    return map[type] || type;
  };
  const { orderPayload, selectedCarrier, carrierSelection, setCarrierSelection, sourceRegionUid, contactDisplayInfo, ruleDispatch, isEditMode, editOrderUid, reset } = useNewOrderStore();

  // GAP-C — fetch rule name for the attribution banner. Cached by React Query.
  const { data: rulesResp } = useListShippingRules(!!ruleDispatch);
  const dispatchRuleName = useMemo(() => {
    if (!ruleDispatch?.winningRuleUid) return undefined;
    return rulesResp?.object?.find((r) => r.uid === ruleDispatch.winningRuleUid)?.name;
  }, [ruleDispatch, rulesResp]);
  const { resetProducts } = useProductStore();
  const { mutate: createOrder } = useCreateOrder();
  const { mutate: updateOrder } = useUpdateOrder(editOrderUid ?? '');
  const { mutateAsync: fetchCarriers } = useGetAvailableCarriers();
  const { data: pickupAddressesData } = useFetchPickupAddresses();

  const [submitting, setSubmitting] = useState<'draft' | 'ship' | undefined>(undefined);
  const [isScrolledUp, setIsScrolledUp] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const retryCountRef = useRef(0);

  useEffect(() => {
    // Only redirect to step 1 if data is missing AND we're not in the middle of submitting
    // (reset() clears the store on success, which would trigger this before navigate fires)
    if (!orderPayload || !selectedCarrier) {
      if (!submitting) {
        navigate(isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER, { replace: true });
      }
    }
  }, [orderPayload, selectedCarrier, navigate, isEditMode, editOrderUid, submitting]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const handleScroll = () => {
      const isAtBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 20;
      setIsScrolledUp(!isAtBottom);
    };
    handleScroll();
    el.addEventListener('scroll', handleScroll);
    return () => el.removeEventListener('scroll', handleScroll);
  }, []);

  if (!orderPayload || !selectedCarrier) return null;

  const contact = orderPayload.contact;
  const products = orderPayload.products ?? [];
  const totalProducts = products.reduce(
    (sum: number, p: any) => sum + (parseFloat(p.price) || 0) * (p.quantity || 1),
    0,
  );

  // Resolve source_region_uid from store or pickup addresses
  const resolveSourceRegionUid = (): string | undefined => {
    if (sourceRegionUid) return sourceRegionUid;
    if (!orderPayload) return undefined;
    const addr = pickupAddressesData?.object?.items?.find(
      (a) => a.uid === orderPayload.saved_pick_up_address_uid || a.uid === orderPayload.pick_up_address_uid,
    );
    return addr?.region?.uid;
  };

  // Silently refresh the carrier session and retry order submission
  const refreshSessionAndRetry = async (readyToShip: boolean) => {
    const regionUid = resolveSourceRegionUid();
    if (!orderPayload || !regionUid || !selectedCarrier) {
      setSubmitting(undefined);
      message.warning(String(t('carrier_session_expired', 'Carrier selection expired. Please select again.')));
      navigate(ROUTE.SELECT_CARRIER, { replace: true });
      return;
    }

    try {
      const carrierRequest = buildAvailableCarriersRequest(orderPayload, regionUid);
      const response: AvailableCarriersAdaptedResponse = await fetchCarriers(carrierRequest);

      // Rebuild available carriers list
      const allRawCarriers = [
        ...(response.object?.same_day_carriers ?? []),
        ...(response.object?.next_day_carriers ?? []),
      ];

      const available: AvailableCarrierItem[] = allRawCarriers.map((c: any) => ({
        carrier_slug: c.carrier?.slug || '',
        carrier_name: c.carrier?.title || '',
        carrier_logo_url: c.carrier?.avatar?.abs_url || '',
        shipping_fee: parseFloat(c.shipping_fee) || 0,
        delivery_day: c.serviceType === ServiceTypeEnum.SAME_DAY || c.average_delivery_time === 'same_day' ? 'same' as const : 'next' as const,
        same_day_cutoff: c.serviceType === ServiceTypeEnum.SAME_DAY ? c.if_ship_before : null,
        drop_off_time: c.serviceType === ServiceTypeEnum.NEXT_DAY ? c.if_ship_before : null,
      }));

      // Verify the selected carrier is still available
      const stillAvailable = available.some(
        (c) => c.carrier_slug === selectedCarrier.carrier_slug && c.delivery_day === selectedCarrier.delivery_day,
      );

      if (!stillAvailable) {
        setSubmitting(undefined);
        message.warning(String(t('carrier_no_longer_available', 'Selected carrier is no longer available. Please select again.')));
        setCarrierSelection({
          process_id: response.process_id,
          recommended_carrier_slug: response.recommended_carrier_slug,
          available_carriers: available,
        });
        navigate(ROUTE.SELECT_CARRIER, { replace: true });
        return;
      }

      // Update store with new session
      setCarrierSelection({
        process_id: response.process_id,
        recommended_carrier_slug: response.recommended_carrier_slug,
        available_carriers: available,
      });

      // Retry the submission with the new process_id
      submitOrder(readyToShip, response.process_id);
    } catch {
      setSubmitting(undefined);
      message.warning(String(t('carrier_session_expired', 'Carrier selection expired. Please select again.')));
      navigate(ROUTE.SELECT_CARRIER, { replace: true });
    }
  };

  // Core order submission logic
  const submitOrder = (readyToShip: boolean, processId?: string) => {
    const activeProcessId = processId || carrierSelection?.process_id;

    const enrichedPayload = {
      ...orderPayload,
      ready_to_pick_up: readyToShip,
      ...(activeProcessId && selectedCarrier.carrier_slug
        ? {
            process_id: activeProcessId,
            selected_carrier_slug: selectedCarrier.carrier_slug,
          }
        : {}),
    };

    const mutateFn = isEditMode ? updateOrder : createOrder;
    mutateFn(enrichedPayload, {
      onSuccess: () => {
        retryCountRef.current = 0;
        message.success(String(t(isEditMode ? 'order_edited_successfully' : 'order_added_successfully')));
        queryClient.invalidateQueries(['orders']);
        queryClient.invalidateQueries(['order']);
        queryClient.invalidateQueries(['pickups']);
        queryClient.invalidateQueries(['order-count']);
        // Navigate FIRST, then reset store — reset triggers the guard useEffect
        // which would redirect to step 1 if we reset before navigating
        const destination = readyToShip
          ? ROUTE.PICKUPS
          : `${ROUTE.ORDERS}?activeTab=ready_to_ship`;
        navigate(destination, { replace: true });
        reset();
        resetProducts(); // O-7: Also reset product store so next create order starts fresh
        setSubmitting(undefined);
      },
      onError: (error: any) => {
        const errorCode = error?.response?.data?.code;
        const errorMsg = error?.response?.data?.message || '';
        const isSessionExpired = errorCode === 300010 || errorMsg.includes('session has expired') || errorMsg.includes('already been used');

        if (isSessionExpired && retryCountRef.current < 1) {
          retryCountRef.current += 1;
          refreshSessionAndRetry(readyToShip);
          return;
        }

        // TEMP-5: temperature-control rejections carry a bilingual, actionable
        // server message — make sure the merchant sees IT rather than field-level
        // 422 arrays. The shared mutation handler (handleErrorMessage) already
        // toasts error.message when the envelope has no field-error object, so
        // only toast here when that object path would have swallowed the message.
        const isTempControlError =
          errorCode === 'TEMP_CONTROL_NO_ELIGIBLE_CARRIER' || errorCode === 'TEMP_CONTROL_LOCKED';
        if (isTempControlError && errorMsg) {
          const fieldErrors = error?.response?.data?.object;
          const messageSwallowedByFieldArrays =
            !!fieldErrors && Object.values(fieldErrors).some((v) => Array.isArray(v));
          if (messageSwallowedByFieldArrays) {
            message.error(errorMsg);
          }
        }

        retryCountRef.current = 0;
        setSubmitting(undefined);
      },
    });
  };

  const handleSubmit = (readyToShip: boolean) => {
    retryCountRef.current = 0;
    setSubmitting(readyToShip ? 'ship' : 'draft');
    submitOrder(readyToShip);
  };

  return (
    <div className='sd-scope sd-font relative flex flex-col -mx-4 sm:-mx-8 -mt-6 -mb-6 h-[calc(100dvh-65px)] overflow-hidden'>
      {/* Content */}
      <div ref={scrollRef} className='flex-1 overflow-y-auto px-4 sm:px-8 pt-4 sm:pt-6 pb-28'>
        {/* Breadcrumb */}
        <nav className='flex items-center gap-1.5 text-sm text-sd-muted-foreground mb-6'>
          <Link to={ROUTE.ORDERS} className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'>
            {String(t('orders'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <Link
            to={isEditMode && editOrderUid ? `/orders/edit/${editOrderUid}` : ROUTE.ADDNEWORDER}
            className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
          >
            {String(t(isEditMode ? 'edit_order' : 'new_order'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <Link to={ROUTE.SELECT_CARRIER} className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'>
            {String(t('select_carrier'))}
          </Link>
          <ChevronRight className={cn('h-3.5 w-3.5', isRTL && 'rotate-180')} strokeWidth={1.5} />
          <span className='text-sd-foreground font-medium'>{String(t('summary'))}</span>
        </nav>

        <OrderStepper currentStep={3} />

        {/* GAP-C — Rule attribution banner. Mirrors the Step-2 banner so
            merchants who land here directly via an auto-dispatched rule
            still see the source of the carrier choice. */}
        {ruleDispatch && selectedCarrier && (() => {
          const banner = buildAutoDispatchBannerText(
            { carrierName: selectedCarrier.carrier_name, ruleName: dispatchRuleName },
            (k, opts) => String(t(k, opts as Record<string, unknown>)),
          );
          return (
            <div className='mt-4 rounded-xl border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3'>
              <Zap className='h-5 w-5 shrink-0 text-blue-600 mt-0.5' strokeWidth={2} />
              <div className='flex-1 min-w-0'>
                <p className='text-sm font-medium text-blue-900'>{banner.title}</p>
              </div>
            </div>
          );
        })()}

        <div className='grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-6'>
          {/* Left — Order Details */}
          <div className='flex flex-col gap-6'>
            {/* Customer */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={User} title={String(t('customer_details', 'Customer Details'))} />
              <div className='flex flex-col divide-y divide-sd-border'>
                <SummaryRow label={String(t('name', 'Name'))} value={contact?.full_name ?? '—'} />
                <SummaryRow label={String(t('primary_phone', 'Primary Phone'))} value={formatIraqiPhone(contact?.phone)} />
                {contact?.additional_phones?.[0] && (
                  <SummaryRow label={String(t('secondary_phone', 'Secondary Phone'))} value={formatIraqiPhone(contact.additional_phones[0])} />
                )}
              </div>
            </section>

            {/* Delivery Address */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={MapPin} title={String(t('delivery_address', 'Delivery Address'))} />
              <div className='flex flex-col divide-y divide-sd-border'>
                {contactDisplayInfo?.provinceName && (
                  <SummaryRow label={String(t('province', 'Province'))} value={contactDisplayInfo.provinceName} />
                )}
                {contactDisplayInfo?.areaName && (
                  <SummaryRow label={String(t('area', 'Area'))} value={contactDisplayInfo.areaName} />
                )}
                <SummaryRow label={String(t('address', 'Address'))} value={contact?.address_text ?? '—'} />
                {orderPayload.description && (
                  <SummaryRow label={String(t('instructions', 'Instructions'))} value={orderPayload.description} />
                )}
              </div>
            </section>

            {/* Products */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={Package} title={String(t('products', 'Products'))} />
              <div className='flex flex-col gap-2'>
                {products.map((product: any, i: number) => (
                  <div
                    key={i}
                    className='flex items-center justify-between rounded-lg bg-sd-muted/30 px-4 py-3'
                  >
                    <div>
                      <p className='text-sm font-medium'>{product.title ?? product.name}</p>
                      <p className='text-xs text-sd-muted-foreground'>{String(t('qty', 'Qty'))}: {product.quantity}</p>
                    </div>
                    <span className='text-sm font-semibold tabular-nums' dir='ltr'>
                      {(
                        (parseFloat(product.price) || 0) * (product.quantity || 1)
                      ).toLocaleString()}{' '}
                      {String(t('iqd'))}
                    </span>
                  </div>
                ))}
              </div>
            </section>

            {/* Carrier */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={Truck} title={String(t('selected_carrier_title', 'Selected Carrier'))} />
              <div className='flex flex-col divide-y divide-sd-border'>
                <div className='flex items-center justify-between py-2'>
                  <span className='text-sm text-sd-muted-foreground'>{String(t('carrier'))}</span>
                  <div className='flex items-center gap-2'>
                    {selectedCarrier.carrier_logo_url && (
                      <img
                        src={selectedCarrier.carrier_logo_url}
                        alt={titleCase(selectedCarrier.carrier_name)}
                        className='h-6 w-6 rounded-full object-contain'
                        onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
                      />
                    )}
                    <span className='text-sm font-medium'>{String(t(normalizeToTranslationKey(selectedCarrier.carrier_name)))}</span>
                  </div>
                </div>
                <SummaryRow
                  label={String(t('delivery_date', 'Delivery Date'))}
                  value={(() => {
                    const d = new Date();
                    if (selectedCarrier.delivery_day !== 'same') d.setDate(d.getDate() + 1);
                    return d.toLocaleDateString(isRTL ? 'ar-IQ' : 'en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
                  })()}
                />
                <SummaryRow
                  label={String(t('type', 'Type'))}
                  value={selectedCarrier.delivery_day === 'same' ? String(t('same_day_delivery', 'Same-day delivery')) : String(t('guaranteed_delivery', 'Guaranteed delivery'))}
                />
              </div>
            </section>

            {/* Order Info */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={FileText} title={String(t('order_info', 'Order Info'))} />
              <div className='flex flex-col divide-y divide-sd-border'>
                <div className='flex items-start justify-between py-2'>
                  <span className='text-sm text-sd-muted-foreground'>{String(t('package_size', 'Package Size'))}</span>
                  <div className='flex items-center gap-1.5'>
                    <span className='text-sm font-medium'>{getSizeLabel(orderPayload.size)}</span>
                    {orderPayload.is_fragile && (
                      <img src={fragileIcon} alt='Fragile' className='h-4 w-4' />
                    )}
                    {/* TEMP-12 — cold-chain icon, exact parity with the
                        fragile <img> beside it: bare 16px glyph, no label,
                        name carried by title/aria-label. */}
                    {orderPayload.temperature_control && (
                      <span
                        data-testid='temperature-control-badge'
                        title={String(t('temperature_controlled'))}
                        aria-label={String(t('temperature_controlled'))}
                        className='inline-flex shrink-0'
                      >
                        <Snowflake className='h-4 w-4 text-blue-500' strokeWidth={2} />
                      </span>
                    )}
                  </div>
                </div>
                <SummaryRow label={String(t('order_type', 'Order Type'))} value={getOrderTypeLabel(orderPayload.type)} />
                <SummaryRow label={String(t('payment', 'Payment'))} value={getPaymentLabel(orderPayload.payment_type)} />
                <SummaryRow label={String(t('pickup', 'Pickup'))} value={getPickupLabel(orderPayload.pick_up_type)} />
                {orderPayload.custom_id && (
                  <SummaryRow label={String(t('custom_id', 'Custom ID'))} value={orderPayload.custom_id} />
                )}
              </div>
            </section>
          </div>

          {/* Right — Sticky Cost Sidebar */}
          <div className='lg:sticky lg:top-6 self-start flex flex-col gap-6'>
            {/* Cost Breakdown */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <SectionHeader icon={CreditCard} title={String(t('cost_summary', 'Cost Summary'))} />
              <div className='flex flex-col divide-y divide-sd-border'>
                {products.map((product: any, i: number) => (
                  <div key={i} className='flex items-center justify-between py-2'>
                    <span className='text-sm text-sd-muted-foreground truncate me-2'>
                      {product.title ?? product.name} × {product.quantity}
                    </span>
                    <span className='text-sm font-medium shrink-0 tabular-nums' dir='ltr'>
                      {(
                        (parseFloat(product.price) || 0) * (product.quantity || 1)
                      ).toLocaleString()}{' '}
                      {String(t('iqd'))}
                    </span>
                  </div>
                ))}
                <div className='flex items-center justify-between py-2'>
                  <span className='text-sm text-sd-muted-foreground'>{String(t('subtotal', 'Subtotal'))}</span>
                  <span className='text-sm font-medium tabular-nums' dir='ltr'>{totalProducts.toLocaleString()} {String(t('iqd'))}</span>
                </div>
                <div className='flex items-center justify-between py-2'>
                  <span className='text-sm text-sd-muted-foreground'>
                    {String(t('shipping_label', 'Shipping'))} ({String(t(normalizeToTranslationKey(selectedCarrier.carrier_name)))})
                  </span>
                  <span className='text-sm font-medium tabular-nums' dir='ltr'>
                    {orderPayload.shipment_fee_type === 'BY_MERCHANT' ? '-' : ''}
                    {Math.round(selectedCarrier.shipping_fee).toLocaleString()} {String(t('iqd'))}
                  </span>
                </div>
                <div className='flex items-center justify-between py-3'>
                  <span className='text-base font-bold text-sd-foreground'>{String(t('total', 'Total'))}</span>
                  <span className='text-base font-bold text-sd-foreground tabular-nums' dir='ltr'>
                    {(() => {
                      // Total (merchant's net) = product_value - shipping_fee (ALWAYS)
                      const total = totalProducts - selectedCarrier.shipping_fee;
                      return `${Math.round(total).toLocaleString()} ${String(t('iqd'))}`;
                    })()}
                  </span>
                </div>
              </div>
            </section>

            {/* Payment Info */}
            <section className='rounded-2xl border border-sd-border bg-sd-card p-5 shadow-sm'>
              <div className='flex items-center justify-between'>
                <span className='text-sm text-sd-muted-foreground'>{String(t('payment_method', 'Payment Method'))}</span>
                <span className='text-sm font-semibold text-sd-foreground'>
                  {getPaymentLabel(orderPayload.payment_type)}
                </span>
              </div>
              <div className='border-t border-x-0 border-b-0 border-dashed border-sd-border mt-3 pt-3 flex items-center justify-between'>
                <span className='text-sm font-semibold text-sd-foreground'>{String(t('customer_will_pay', 'Customer Will Pay'))}</span>
                <span className='text-sm font-bold text-sd-foreground tabular-nums' dir='ltr'>
                  {(() => {
                    // PREPAID: customer already paid — owes nothing at delivery
                    if (orderPayload.payment_type === 'PREPAID') {
                      return `0 ${String(t('iqd'))}`;
                    }
                    // COLLECT_ON_DELIVERY: customer pays product value
                    // plus shipping only when shipment_fee_type is BY_CUSTOMER
                    const shippingOwedByCustomer =
                      orderPayload.shipment_fee_type === 'BY_CUSTOMER'
                        ? selectedCarrier.shipping_fee
                        : 0;
                    return `${Math.round(totalProducts + shippingOwedByCustomer).toLocaleString()} ${String(t('iqd'))}`;
                  })()}
                </span>
              </div>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='absolute bottom-4 start-0 end-0 z-30 px-4 sm:px-8'>
        <div
          className={cn(
            'rounded-2xl border border-sd-border bg-sd-card px-5 py-4 flex items-center justify-between gap-3 transition-shadow',
            isScrolledUp && 'shadow-lg',
          )}
        >
          <Link
            to={ROUTE.SELECT_CARRIER}
            className='inline-flex items-center justify-center whitespace-nowrap gap-2 h-10 rounded-md border border-sd-border bg-transparent px-4 text-sm font-medium uppercase text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 transition-colors'
          >
            <ArrowLeft className={cn('h-4 w-4', isRTL && 'rotate-180')} strokeWidth={2} />
            {String(t('back', 'BACK'))}
          </Link>
          <div className='flex items-center gap-3'>
            {isEditMode ? (
              <button
                onClick={() => handleSubmit(false)}
                disabled={!!submitting}
                className='inline-flex items-center justify-center whitespace-nowrap gap-2 h-10 rounded-md bg-sd-primary px-4 text-sm font-medium uppercase text-sd-primary-foreground hover:bg-sd-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 disabled:opacity-50 transition-colors'
              >
                {submitting ? '...' : String(t('update', 'UPDATE'))}
              </button>
            ) : (
              <>
                <button
                  onClick={() => handleSubmit(false)}
                  disabled={!!submitting}
                  className='inline-flex items-center justify-center whitespace-nowrap h-10 rounded-md border border-sd-border bg-transparent px-4 text-sm font-medium uppercase text-sd-foreground hover:bg-sd-accent hover:text-sd-accent-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 disabled:opacity-50 transition-colors'
                >
                  {submitting === 'draft' ? '...' : String(t('save_as_draft', 'SAVE AS DRAFT'))}
                </button>
                <button
                  onClick={() => handleSubmit(true)}
                  disabled={!!submitting}
                  className='inline-flex items-center justify-center whitespace-nowrap gap-2 h-10 rounded-md bg-sd-primary px-4 text-sm font-medium uppercase text-sd-primary-foreground hover:bg-sd-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sd-ring focus-visible:ring-offset-2 disabled:opacity-50 transition-colors'
                >
                  <Truck className='h-4 w-4' strokeWidth={1.5} />
                  {submitting === 'ship' ? '...' : String(t('ship_now', 'SHIP NOW'))}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
